/* eslint-disable vue/valid-v-slot */
<template>
    <div class="rma-history-page-content">
        <header class="rma-history__heading">
            <div>
                <span class="rma-history__eyebrow">YOUR ACCOUNT / SERVICE</span>
                <h2>Repair history</h2>
                <p>Review repair progress, service dates, and return tracking.</p>
            </div>
            <div class="rma-history__heading-actions">
                <span v-if="historyTotal !== null" class="rma-history__count">{{ historyTotal }} requests</span>
                <download-file-xlsx
                    class="rma-history__download"
                    :text="button"
                    :table-array="downloadTableData"
                    :table-header="downloadTableHeader"
                    :file-name="filename"
                    :data-func="downloadData"
                    color="primary"
                    loader
                />
            </div>
        </header>

        <section class="rma-history">
            <v-alert v-if="historyError" class="rma-history__error" type="error" outlined dense role="alert">
                {{ historyError }}
                <v-btn text small @click="fetchRmas">Try again</v-btn>
            </v-alert>
            <v-alert v-if="detailsError" class="rma-history__error" type="error" outlined dense role="alert">
                {{ detailsError }}
            </v-alert>

            <section class="rma-history__filters" aria-label="Filter repair history">
                <v-select
                    v-model="year"
                    class="rma-history__filter"
                    label="Year"
                    :items="years"
                    dark
                    hide-details
                    clearable
                />
                <v-select
                    v-model="month"
                    class="rma-history__filter"
                    label="Month"
                    :items="months"
                    item-text="text"
                    item-value="value"
                    hide-details
                    dark
                    clearable
                />
                <v-text-field
                    v-model="search"
                    v-debounce="searchFromFilter"
                    class="rma-history__filter rma-history__filter--search"
                    :append-icon="mdiMagnify"
                    label="Search by RMA, serial, model, or issue"
                    single-line
                    clearable
                    dark
                    hide-details
                    @click:clear="clearFilter"
                />
            </section>

            <div class="rma-history__table-wrap">
                <v-data-table
                    class="rma-history__table"
                    :class="{ 'rma-history__table--with-user': headers.length > headerBase.length }"
                    :server-items-length="items && items[0] && items[0].itemCount"
                    :loading="rmasLoading"
                    :headers="headers"
                    :options.sync="options"
                    :items="items"
                    :mobile-breakpoint="0"
                    :no-data-text="
                        historyError ? 'Repair history could not be loaded.' : 'No repair requests match these filters.'
                    "
                    data-cy="rma-history-table"
                    :footer-props="optionsDepOnBrowser"
                    dark
                >
                    <template v-slot:group.header="{ remove, toggle, group, groupBy }">
                        <td :key="group" colspan="9" class="rma-history__group" @click="toggle">
                            <v-btn icon x-small aria-label="Collapse group"
                                ><v-icon>{{ mdiMinus }}</v-icon></v-btn
                            >
                            <strong>{{ groupByDisplay(groupBy) }}:</strong> {{ group }}
                            <v-btn icon x-small aria-label="Remove grouping" @click.stop="remove"
                                ><v-icon>{{ mdiClose }}</v-icon></v-btn
                            >
                        </td>
                    </template>

                    <template v-slot:item.problem="{ item, value }">
                        <span>{{ value || '—' }}</span>
                        <v-tooltip v-if="item.additionalProblems && item.additionalProblems.length" bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <button type="button" class="rma-history__more" v-bind="attrs" v-on="on">
                                    +{{ item.additionalProblems.length }} more
                                </button>
                            </template>
                            <span v-for="problem in item.additionalProblems" :key="problem">{{ problem }}<br /></span>
                        </v-tooltip>
                    </template>

                    <template v-slot:item.createdAt="{ item }">{{ formatDate(item.createdAt) }}</template>
                    <template v-slot:item.closedAt="{ item }">{{ formatDate(item.closedAt) }}</template>
                    <template v-slot:item.unit.serial="{ item }">
                        <span :title="item.unit.serial" :data-cy="`rma-serial-${item.unit.serial}`">{{
                            item.unit.serial || '—'
                        }}</span>
                    </template>
                    <template v-slot:item.unit.model="{ item }">
                        <span :title="item.unit.model">{{ item.unit.model || '—' }}</span>
                    </template>
                    <template v-slot:item.primaryFault="{ item }">
                        <span :title="item.primaryFault">{{ item.primaryFault || '—' }}</span>
                    </template>
                    <template v-slot:item.customerStatus="{ item }">
                        <span
                            class="rma-history__status"
                            :data-cy="`rma-status-${item.uid}`"
                            :title="item.customerStatus || 'Status unavailable'"
                        >
                            <span aria-hidden="true"></span
                            ><span class="rma-history__cell-text">{{
                                item.customerStatus || 'Status unavailable'
                            }}</span>
                        </span>
                    </template>
                    <template v-slot:item.rmaType="{ item }">
                        <span
                            class="rma-history__type"
                            :class="{ 'rma-history__type--exchange': item.rmaType === 'GVS_ADVANCE_EXCHANGE' }"
                        >
                            {{ item.rmaType === 'GVS_ADVANCE_EXCHANGE' ? 'Advance exchange' : 'Standard' }}
                        </span>
                    </template>
                    <template v-slot:item.uid="{ item }">
                        <button
                            type="button"
                            class="rma-history__open rma-history__open--uid"
                            :data-cy="`link-open-rma-details-${item.uid}`"
                            :disabled="!!detailsLoading"
                            @click="exposeRmaDetails(item.uid)"
                        >
                            {{ item.uid }}
                            <v-progress-circular v-if="detailsLoading === item.uid" indeterminate size="13" width="2" />
                        </button>
                    </template>
                    <template v-slot:item.tracking.inbound.trackingNumber="{ item }">
                        <a
                            v-if="item.tracking && item.tracking.inbound && item.tracking.inbound.trackingNumber"
                            :href="item.tracking.inbound.trackingUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            :title="item.tracking.inbound.trackingNumber"
                            >{{ item.tracking.inbound.trackingNumber }}</a
                        >
                        <span v-else>—</span>
                    </template>
                    <template v-slot:item.tracking.outbound.trackingNumber="{ item }">
                        <a
                            v-if="item.tracking && item.tracking.outbound && item.tracking.outbound.trackingNumber"
                            :href="item.tracking.outbound.trackingUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            :title="item.tracking.outbound.trackingNumber"
                            >{{ item.tracking.outbound.trackingNumber }}</a
                        >
                        <span v-else>—</span>
                    </template>
                </v-data-table>
            </div>
        </section>

        <rma-details-dialog ref="serviceDetails" :rma-values="singleRmaDetails" @reloadHistory="fetchRmas" />
    </div>
</template>

<script>
import { rmaHistoryByUserId, rmaDetailsByUid } from '@/api'
import { mdiMinus, mdiClose, mdiMagnify } from '@mdi/js'
import { formatDateShort } from '@/utils'
import RmaDetailsDialog from '@/views/account_dashboard/components/rma_details_dialog.vue'
import DownloadFileXlsx from '../../../components/button/download_file_xlsx.vue'

export default {
    name: `RmaHistoryTable`,

    components: {
        RmaDetailsDialog,
        DownloadFileXlsx
    },

    // metaInfo: { title: `RMA History` },

    data() {
        return {
            items: [],
            mdiMagnify,
            mdiMinus,
            mdiClose,
            rmasLoading: false,
            historyError: '',
            detailsError: '',
            detailsLoading: '',
            search: ``,
            singleRmaDetails: {},
            options: {
                sortBy: [`createdAt`],
                sortDesc: [true]
            },
            serialDetailsDialog: false,
            years: [],
            year: '',
            month: '',
            months: Array.from({ length: 12 }, (_, i) => ({
                text: new Date(0, i).toLocaleString('en-US', { month: 'short' }),
                value: i + 1
            })),
            isDownload: false,
            downloadTableHeader: [],
            downloadTableData: [],
            earliestDate: null,
            filename: 'RmaHistory.xlsx',
            button: 'Download RMA History',
            headerBase: [
                {
                    text: `RMA`,
                    value: `uid`,
                    groupable: false,
                    sortable: true
                },
                {
                    text: 'Advance Exchange(?)',
                    value: 'rmaType',
                    groupable: false,
                    sortable: false,
                    align: 'center'
                },
                {
                    text: `Serial #`,
                    value: `unit.serial`,
                    groupable: false,
                    sortable: true
                },
                {
                    text: `Model`,
                    value: `unit.model`,
                    sortable: true
                },
                {
                    text: `Reported Failure`,
                    value: `primaryFault`,
                    sortable: true
                },

                {
                    text: `Reported Problem`,
                    value: `problem`,
                    sortable: true
                },

                {
                    text: `Status`,
                    value: `customerStatus`,
                    sortable: true,
                    align: 'center'
                },
                {
                    text: `Inbound Tracking#`,
                    value: `tracking.inbound.trackingNumber`,
                    groupable: false
                },
                {
                    text: `Outbound Tracking#`,
                    value: `tracking.outbound.trackingNumber`,
                    groupable: false
                },

                {
                    text: `Created Date`,
                    value: `createdAt`,
                    groupable: false,
                    sortable: true
                },
                {
                    text: `Closed Date`,
                    value: `closedAt`,
                    groupable: false,
                    sortable: true
                }
            ]
        }
    },

    computed: {
        historyTotal() {
            const total = Number(this.items[0]?.itemCount)
            return Number.isFinite(total) ? total : null
        },
        headers() {
            const count = this.items.reduce((acc, curr) => {
                if (curr.username !== undefined) return acc + 1
                return acc
            }, 0)

            if (count > 0)
                return [
                    {
                        text: `User`,
                        value: `username`,
                        sortable: true
                    },
                    ...this.headerBase
                ]
            return this.headerBase
        },
        optionsDepOnBrowser() {
            const ua = navigator.userAgent
            const isIE = ua.indexOf(`MSIE `) > -1 || ua.indexOf(`Trident/`) > -1

            return isIE
                ? {
                      disableItemsPerPage: true,
                      itemsPerPageText: ``,
                      itemsPerPageOptions: [5, 10, 30]
                  }
                : {
                      disableItemsPerPage: false,
                      itemsPerPageText: `Rows Per Page`,
                      itemsPerPageOptions: [5, 10, 30]
                  }
        }
    },
    watch: {
        options: {
            handler() {
                this.fetchRmas()
            },
            deep: true
        },
        year: {
            handler() {
                this.fetchRmas()
            }
        },
        month: {
            handler() {
                this.fetchRmas()
            }
        }
    },
    mounted() {
        this.fetchRmas()
    },
    methods: {
        clearFilter() {
            this.search = ``
            this.searchFromFilter()
        },
        searchFromFilter() {
            this.options.page = 1
            this.fetchRmas()
        },
        formatDate(v) {
            if (!v) return `--`
            else return formatDateShort(new Date(v))
        },
        getYears() {
            const result = []
            if (!this.earliestDate) {
                this.earliestDate = new Date()
            }
            const startYear = this.earliestDate.getFullYear()
            const endDate = new Date()
            const endYear = endDate.getFullYear()

            for (let i = startYear; i <= endYear; i++) {
                result.unshift(i)
            }
            return result
        },

        async fetchRmas() {
            const { sortBy, sortDesc, page, itemsPerPage } = this.options
            const year = this.year
            const month = this.month
            const isDownload = this.isDownload

            if (this.search == null) {
                this.search = ''
            }

            this.rmasLoading = true
            this.historyError = ''
            try {
                const { data } = await rmaHistoryByUserId({
                    sortBy,
                    sortDesc,
                    page,
                    itemsPerPage,
                    filterText: (`` + this.search).trim(),
                    year,
                    month,
                    isDownload
                })
                this.items = Array.isArray(data?.rmas) ? data.rmas : []
                this.earliestDate = this.items[0] ? new Date(this.items[0].earliestDate) : null

                if (!this.years.length) {
                    this.years = this.getYears()
                }
            } catch (error) {
                this.items = []
                this.historyError = 'Your repair history could not be loaded. Please try again.'
            } finally {
                this.rmasLoading = false
            }
        },
        async downloadData() {
            this.isDownload = true
            try {
                await this.fetchRmas()
                const dnTableData = this.items
                if (dnTableData && dnTableData.length > 0) {
                    this.downloadTableHeader = this.headerBase.map(({ text, value }) => ({ text, value }))
                    this.downloadTableData = dnTableData
                }
            } finally {
                this.isDownload = false
                this.fetchRmas()
            }
        },
        async exposeRmaDetails(uid) {
            if (this.detailsLoading) return
            this.detailsLoading = uid
            this.detailsError = ''
            try {
                const { data } = await rmaDetailsByUid({ uid })
                if (!data || !data.uid) throw new Error('RMA details unavailable')
                this.singleRmaDetails = data
                await this.$nextTick()
                this.$refs.serviceDetails.open()
            } catch (error) {
                this.detailsError = 'We could not open this repair request. Please try again.'
            } finally {
                this.detailsLoading = ''
            }
        },
        groupByDisplay(groupBy = []) {
            return groupBy
                .map((group) => {
                    switch (group) {
                        case `problem`:
                            return `Reported Problem`
                        case `unit.model`:
                            return `Model`
                        case `primaryFault`:
                            return `Primary Fault`
                        case `latestStatus`:
                            return `Status`
                        default:
                            return group
                    }
                })
                .toString()
        }
    }
}
</script>
<style scoped>
.rma-history-page-content {
    min-width: 0;
}
.rma-history {
    overflow: hidden;
    color: #f1f2f5;
    background: rgba(35, 38, 46, 0.88);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 12px;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.14);
}
.rma-history__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    margin: 0 0 20px;
    padding: 3px 2px 0;
}
.rma-history__heading > div:first-child {
    position: relative;
    padding-left: 17px;
}
.rma-history__heading > div:first-child::before {
    position: absolute;
    top: 2px;
    bottom: 3px;
    left: 0;
    width: 3px;
    background: #db5120;
    border-radius: 3px;
    content: '';
}
.rma-history__eyebrow {
    color: #ff9a74;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1.4px;
}
.rma-history__heading h2 {
    margin: 5px 0 4px;
    color: #f4f5f8;
    font-size: clamp(24px, 3vw, 32px);
    font-weight: 600;
    letter-spacing: -0.75px;
}
.rma-history__heading p {
    margin: 0;
    color: #aeb5c3;
    font-size: 12px;
    line-height: 1.5;
}
.rma-history__heading-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 0 0 auto;
}
.rma-history__count {
    display: inline-flex;
    align-items: center;
    min-height: 34px;
    padding: 0 10px;
    color: #c7cbd4;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
}
.rma-history__download {
    flex: 0 0 auto;
}
.rma-history__error {
    margin: 16px 20px 0;
}
.rma-history__filters {
    display: grid;
    grid-template-columns: minmax(120px, 0.7fr) minmax(140px, 0.8fr) minmax(240px, 1.7fr);
    align-items: center;
    gap: 12px;
    padding: 15px 20px 13px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.075);
}
.rma-history__filter {
    min-width: 0;
    margin: 0;
    padding: 0;
}
.rma-history__filter ::v-deep .v-input__slot {
    min-height: 40px;
    padding: 0 11px;
    background: rgba(18, 20, 26, 0.36);
    border: 1px solid rgba(255, 255, 255, 0.13);
    border-radius: 6px;
}
.rma-history__filter ::v-deep .v-input__slot::before,
.rma-history__filter ::v-deep .v-input__slot::after {
    display: none;
}
.rma-history__filter ::v-deep .v-label {
    color: #aeb5c3;
    font-size: 11px;
}
.rma-history__filter ::v-deep input,
.rma-history__filter ::v-deep .v-select__selection {
    color: #f1f2f5;
    font-size: 11px;
}
.rma-history__filter ::v-deep .v-icon {
    color: #aeb5c3;
}
.rma-history__filter--search ::v-deep .v-input__slot {
    padding-left: 12px;
}
.rma-history__table-wrap {
    overflow: hidden;
    padding: 0 12px 12px;
}
.rma-history__table {
    font-family: 'Inter Tight', 'Roboto', sans-serif !important;
    background: transparent !important;
}
.rma-history__table ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
    background: #22252d;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    scrollbar-color: #f0784c #20222a;
    scrollbar-width: thin;
}
.rma-history__table ::v-deep .v-data-table__wrapper::-webkit-scrollbar {
    width: 9px;
    height: 9px;
}
.rma-history__table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-track {
    background: #20222a;
    border-radius: 9px;
}
.rma-history__table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #f0784c, #c54820);
    border: 2px solid #20222a;
    border-radius: 9px;
}
.rma-history__table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
    background: #ff9a74;
}
.rma-history__table ::v-deep table {
    width: 100%;
    min-width: 1580px;
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
    font-variant-numeric: tabular-nums;
}
.rma-history__table ::v-deep th:nth-child(1),
.rma-history__table ::v-deep td:nth-child(1) {
    width: 185px;
}
.rma-history__table ::v-deep tbody td:nth-child(1) {
    overflow: visible;
    text-overflow: clip;
}
.rma-history__table ::v-deep tbody td:nth-child(2) {
    overflow: visible;
    text-overflow: clip;
}
.rma-history__table ::v-deep th:nth-child(2),
.rma-history__table ::v-deep td:nth-child(2) {
    width: 160px;
}
.rma-history__table ::v-deep th:nth-child(2) {
    width: 160px;
}
.rma-history__table ::v-deep th:nth-child(3),
.rma-history__table ::v-deep td:nth-child(3) {
    width: 130px;
}
.rma-history__table ::v-deep th:nth-child(4),
.rma-history__table ::v-deep td:nth-child(4) {
    width: 125px;
}
.rma-history__table ::v-deep th:nth-child(5),
.rma-history__table ::v-deep td:nth-child(5) {
    width: 130px;
}
.rma-history__table ::v-deep th:nth-child(6),
.rma-history__table ::v-deep td:nth-child(6) {
    width: 210px;
}
.rma-history__table ::v-deep th:nth-child(7),
.rma-history__table ::v-deep td:nth-child(7) {
    width: 175px;
}
.rma-history__table ::v-deep th:nth-child(7) {
    width: 175px;
}
.rma-history__table ::v-deep th:nth-child(8),
.rma-history__table ::v-deep td:nth-child(8),
.rma-history__table ::v-deep th:nth-child(9),
.rma-history__table ::v-deep td:nth-child(9) {
    width: 150px;
}
.rma-history__table ::v-deep th:nth-child(10),
.rma-history__table ::v-deep td:nth-child(10),
.rma-history__table ::v-deep th:nth-child(11),
.rma-history__table ::v-deep td:nth-child(11) {
    width: 130px;
}
.rma-history__table--with-user ::v-deep table {
    min-width: 1775px;
}
.rma-history__table--with-user ::v-deep th:nth-child(1),
.rma-history__table--with-user ::v-deep td:nth-child(1) {
    width: 140px;
}
.rma-history__table--with-user ::v-deep th:nth-child(2),
.rma-history__table--with-user ::v-deep td:nth-child(2) {
    width: 185px;
}
.rma-history__table--with-user ::v-deep th:nth-child(2) {
    width: 185px;
}
.rma-history__table--with-user ::v-deep tbody td:nth-child(2) {
    overflow: visible;
    text-overflow: clip;
}
.rma-history__table--with-user ::v-deep tbody td:nth-child(3) {
    overflow: visible;
    text-overflow: clip;
}
.rma-history__table--with-user ::v-deep th:nth-child(3),
.rma-history__table--with-user ::v-deep td:nth-child(3) {
    width: 160px;
}
.rma-history__table--with-user ::v-deep th:nth-child(3) {
    width: 160px;
}
.rma-history__table--with-user ::v-deep th:nth-child(4),
.rma-history__table--with-user ::v-deep td:nth-child(4) {
    width: 130px;
}
.rma-history__table--with-user ::v-deep th:nth-child(5),
.rma-history__table--with-user ::v-deep td:nth-child(5) {
    width: 125px;
}
.rma-history__table--with-user ::v-deep th:nth-child(6),
.rma-history__table--with-user ::v-deep td:nth-child(6) {
    width: 130px;
}
.rma-history__table--with-user ::v-deep th:nth-child(7),
.rma-history__table--with-user ::v-deep td:nth-child(7) {
    width: 210px;
}
.rma-history__table--with-user ::v-deep th:nth-child(8),
.rma-history__table--with-user ::v-deep td:nth-child(8) {
    width: 175px;
}
.rma-history__table--with-user ::v-deep th:nth-child(8) {
    width: 175px;
}
.rma-history__table--with-user ::v-deep th:nth-child(9),
.rma-history__table--with-user ::v-deep td:nth-child(9),
.rma-history__table--with-user ::v-deep th:nth-child(10),
.rma-history__table--with-user ::v-deep td:nth-child(10) {
    width: 150px;
}
.rma-history__table--with-user ::v-deep th:nth-child(11),
.rma-history__table--with-user ::v-deep td:nth-child(11),
.rma-history__table--with-user ::v-deep th:nth-child(12),
.rma-history__table--with-user ::v-deep td:nth-child(12) {
    width: 130px;
}
.rma-history__table ::v-deep thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    height: 46px;
    padding: 0 14px;
    color: #d6d9e1 !important;
    background: #30333b;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12) !important;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.05px;
    text-transform: none;
    white-space: nowrap;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
}
.rma-history__table ::v-deep thead th + th {
    box-shadow: inset 1px 0 rgba(255, 255, 255, 0.045);
}
.rma-history__table ::v-deep thead .v-icon {
    color: #ff9a74;
}
.rma-history__table ::v-deep tbody td {
    height: 52px;
    padding: 10px 14px;
    overflow: hidden;
    color: #e2e5ec !important;
    background: #282b33;
    border-bottom: 1px solid rgba(255, 255, 255, 0.055) !important;
    font-size: 11px;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
    line-height: 1.5;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.rma-history__open--uid {
    max-width: none;
    overflow: visible;
    text-overflow: clip;
    white-space: nowrap;
}
.rma-history__table ::v-deep tbody td + td {
    box-shadow: inset 1px 0 rgba(255, 255, 255, 0.025);
}
.rma-history__table ::v-deep tbody tr:nth-child(even) {
    background: transparent;
}
.rma-history__table ::v-deep tbody tr:nth-child(even) td {
    background: #252830;
}
.rma-history__table ::v-deep tbody tr:hover {
    background: transparent !important;
}
.rma-history__table ::v-deep tbody tr:hover td {
    background: rgba(219, 81, 32, 0.12) !important;
}
.rma-history__table ::v-deep tbody tr:last-child td {
    border-bottom: 0 !important;
}
.rma-history__table ::v-deep .v-data-table__empty-wrapper {
    color: #aeb5c3;
}
.rma-history__table ::v-deep .v-data-footer {
    color: #aeb5c3;
    background: transparent;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    font-size: 10px;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
}
.rma-history__table ::v-deep .v-data-footer .v-icon {
    color: #c6cbd5;
}
.rma-history__table ::v-deep .v-data-footer__select,
.rma-history__table ::v-deep .v-data-footer__pagination {
    color: #aeb5c3;
}
.rma-history__table ::v-deep .v-data-footer .v-input__slot::before,
.rma-history__table ::v-deep .v-data-footer .v-input__slot::after {
    display: none;
}
.rma-history__table ::v-deep a {
    color: #ff9a74;
    text-decoration: none;
}
.rma-history__group {
    color: #f2a58b !important;
    background: rgba(219, 81, 32, 0.08);
    font-size: 11px;
}
.rma-history__more,
.rma-history__open {
    padding: 0;
    color: #ff9a74;
    background: none;
    border: 0;
    font: inherit;
    font-size: 10px;
    font-weight: 600;
    cursor: pointer;
}
.rma-history__more {
    margin-left: 5px;
}
.rma-history__open {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
}
.rma-history__open--uid {
    min-height: 30px;
    padding: 2px 4px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.1px;
}
.rma-history__open:hover,
.rma-history__more:hover {
    text-decoration: underline;
}
.rma-history__open:disabled {
    opacity: 0.6;
    cursor: wait;
}
.rma-history__open:focus-visible,
.rma-history__more:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 3px;
}
.rma-history__status,
.rma-history__type {
    display: inline-flex;
    width: 132px;
    min-width: 132px;
    max-width: 132px;
    min-height: 32px;
    box-sizing: border-box;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 5px 8px;
    color: #ffcfbb;
    background: rgba(219, 81, 32, 0.08);
    border: 1px solid rgba(240, 120, 76, 0.2);
    border-radius: 5px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
}
.rma-history__status > span:first-child {
    display: block;
    width: 5px;
    min-width: 5px;
    height: 5px;
    background: #f0784c;
    border-radius: 50%;
}
.rma-history__cell-text {
    display: block;
    min-width: 0;
    max-width: 100%;
    flex: 0 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.rma-history__type {
    color: #c5cbd6;
    background: rgba(255, 255, 255, 0.035);
    border-color: rgba(255, 255, 255, 0.12);
}
.rma-history__type--exchange {
    color: #a8dbba;
    background: rgba(63, 177, 108, 0.08);
    border-color: rgba(88, 201, 135, 0.2);
}
@media (max-width: 760px) {
    .rma-history__filters {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .rma-history__filter--search {
        grid-column: 1 / -1;
    }
}
@media (max-width: 520px) {
    .rma-history__heading {
        align-items: flex-start;
        flex-direction: column;
        gap: 12px;
        margin-bottom: 15px;
    }
    .rma-history__heading-actions {
        width: 100%;
        justify-content: space-between;
    }
    .rma-history__filters {
        grid-template-columns: minmax(0, 1fr);
        padding: 13px 15px;
    }
    .rma-history__filter--search {
        grid-column: auto;
    }
}
</style>
